import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PromptCard from '../components/prompt/PromptCard';
import Pagination from '../components/ui/Pagination';
import { promptService, type Prompt } from '../services/promptService';
import { useSearchResults, useSearchUrl } from '../hooks/queries/useSearch';
import type { PaginatedResponse } from '../services/apiService';
import SEO from '../components/seo/SEO';

interface BrowseState {
  prompts: Prompt[];
  pagination: PaginatedResponse<Prompt>['pagination'] | null;
  isLoading: boolean;
}

export default function BrowsePrompts() {
  const location = useLocation();
  const navigate = useNavigate();
  const { parseUrl } = useSearchUrl();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const parsedFilters = useMemo(() => parseUrl(searchParams), [parseUrl, searchParams]);

  const [query, setQuery] = useState(parsedFilters.query ?? '');

  const buildParams = (nextQuery: string, page?: number) => {
    const params = new URLSearchParams();
    const trimmed = nextQuery.trim();

    if (trimmed) {
      params.set('query', trimmed);
    }

    if (page) {
      params.set('page', page.toString());
    }

    return params;
  };

  useEffect(() => {
    setQuery(parsedFilters.query ?? '');
  }, [parsedFilters.query]);

  const hasQuery = Boolean((parsedFilters.query ?? '').trim());

  const { data: searchResults, isLoading: isSearchLoading, isFetching: isSearchFetching } = useSearchResults(
    {
      query: parsedFilters.query ?? '',
      page: parsedFilters.page,
      per_page: parsedFilters.per_page,
    },
    {
      enabled: hasQuery,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  );

  const {
    data: browseResults,
    isLoading: isBrowseLoading,
    isFetching: isBrowseFetching,
  } = useQuery<PaginatedResponse<Prompt>>({
    queryKey: ['prompts', 'browse', parsedFilters.page ?? 1, parsedFilters.per_page],
    queryFn: async () =>
      promptService.getPrompts({
        page: parsedFilters.page,
        per_page: parsedFilters.per_page,
      }),
    enabled: !hasQuery,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const params = buildParams(query);
    const search = params.toString();
    navigate(search ? `/prompts?${search}` : '/prompts');
  };

  const handlePageChange = (page: number) => {
    const params = buildParams(query, page);
    const search = params.toString();
    navigate(search ? `/prompts?${search}` : '/prompts');
  };

  const activeResults: BrowseState = useMemo(() => {
    if (hasQuery) {
      return {
        prompts: searchResults?.prompts ?? [],
        pagination: searchResults?.pagination ?? null,
        isLoading: isSearchLoading || isSearchFetching,
      };
    }

    return {
      prompts: browseResults?.data ?? [],
      pagination: browseResults?.pagination ?? null,
      isLoading: isBrowseLoading || isBrowseFetching,
    };
  }, [
    hasQuery,
    searchResults,
    isSearchLoading,
    isSearchFetching,
    browseResults,
    isBrowseLoading,
    isBrowseFetching,
  ]);

  const seoTitle = hasQuery
    ? `Search: "${parsedFilters.query}" | Browse AI Prompts`
    : 'Browse AI Prompts | Midjourney, ChatGPT, DALL-E';

  const seoDescription = hasQuery
    ? `Search results for "${parsedFilters.query}" prompts. Copy, save, or discover related prompts on PromptShare.`
    : 'Search and filter through thousands of creative AI prompts from top prompt engineers around the world. Copy prompts for Midjourney, ChatGPT, DALL-E, and Stable Diffusion.';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title={seoTitle}
        description={seoDescription}
        url={`https://promptshare.pro/prompts${location.search}`}
      />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Browse Prompts</h1>
          <p className="text-gray-600 mt-1">Search and explore prompts from the community</p>
        </div>
        <form onSubmit={handleSubmit} className="w-full sm:w-auto sm:min-w-[360px]">
          <div className="relative">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search prompts..."
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-20 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              aria-label="Search prompts"
              autoComplete="off"
            />
            <button
              type="submit"
              className="absolute inset-y-0 right-0 px-4 bg-primary-600 text-white rounded-r-lg hover:bg-primary-700 transition-colors font-medium"
              aria-label="Search"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {activeResults.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 h-64 rounded-lg mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : activeResults.prompts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-600 text-lg">No prompts found</p>
          <p className="text-gray-500 text-sm mt-2">Try adjusting your search or browse all prompts</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {activeResults.prompts.map((prompt) => (
            <PromptCard key={prompt.public_id} prompt={prompt} />
          ))}
        </div>
      )}

      {activeResults.pagination && activeResults.pagination.last_page > 1 && (
        <Pagination
          currentPage={activeResults.pagination.current_page}
          lastPage={activeResults.pagination.last_page}
          onPageChange={handlePageChange}
          className="mt-10"
        />
      )}
    </div>
  );
}
